// frontend/src/components/VetStartedSOAPTabs.js (Full updated file)

import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect, useCallback } from "react";
import Subjective from "./Subjective";
import Objective from "./Objective";
import Assessment from "./Assessments";
import Plan from "./Plan";
import BillingTab from "./Billing";
import { PrescriptionProvider } from "./PrescriptionContext";
import { httpService } from "../../services/httpService";
import { BillingProvider, useBilling } from "./BillingContext";
import { toast } from "react-toastify";
import { CheckIcon } from "lucide-react";

// ─── Auto-save status indicator component ──────────────────────────────────
const AutoSaveStatus = ({ status, isSaving }) => {
  const getStatusDisplay = () => {
    if (isSaving || status === "saving") {
      return {
        icon: (
          <div className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        ),
        text: "Auto-saving...",
        className: "text-amber-600"
      };
    }
    if (status === "dirty") {
      return {
        icon: <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse" />,
        text: "Unsaved changes",
        className: "text-amber-600"
      };
    }
    return {
      icon: <CheckIcon className="w-3.5 h-3.5 text-emerald-500" />,
      text: "All saved ✓",
      className: "text-emerald-600"
    };
  };

  const display = getStatusDisplay();

  return (
    <div className={`flex items-center gap-1.5 text-[11px] font-semibold ${display.className}`}>
      {display.icon}
      <span>{display.text}</span>
    </div>
  );
};

// ─── Outer wrapper: sets up both PrescriptionProvider and BillingProvider ──
const VetStartedSOAPTabs = forwardRef((props, ref) => {
  const { refId, vendorPetId, orderId, initialData } = props;

  return (
    <PrescriptionProvider
      initialData={initialData}
      refId={refId}
      orderId={orderId}
      vendorPetId={vendorPetId}
    >
      <BillingProvider refId={refId}>
        <VetStartedSOAPTabsInner {...props} forwardedRef={ref} />
      </BillingProvider>
    </PrescriptionProvider>
  );
});

VetStartedSOAPTabs.displayName = "VetStartedSOAPTabs";

// ─── Inner component ────────────────────────────────────────────────────────
const VetStartedSOAPTabsInner = ({
  refId,
  vendorPetId,
  orderId,
  bookingStatus,
  readOnly = false,
  onDirtyChange,
  consultationFee = 0,
  initialData,
  forwardedRef,
  refreshBilling: externalRefreshBilling,
  onBillingChange,
  onBillingSummaryChange,
}) => {
  // ⭐ FIX: Start with only "S" open
  const [expandedSections, setExpandedSections] = useState(["S"]);
  const [activeTab, setActiveTab] = useState("S");
  // const [isScrolling, setIsScrolling] = useState(false);
  const [billingSummary, setBillingSummary] = useState(null);
  const scrollContainerRef = useRef(null);
  const [billingRefreshKey, setBillingRefreshKey] = useState(0);
  const [planRefreshKey, setPlanRefreshKey] = useState(0);
  const [currentInitialData, setCurrentInitialData] = useState(initialData);
  const [isRefreshingPlan, setIsRefreshingPlan] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState("saved");
  const lastRefreshTime = useRef(0);
  const isHydratingRef = useRef(false);
  const programmaticScrollRef = useRef(false);
  const autoSaveTimerRef = useRef(null);
  const isAutoSavingRef = useRef(false);
  const lastSavedDataRef = useRef(null);
  const isInitialMountRef = useRef(true);
  const isSessionHydratingRef = useRef(true);
  const hydrationTimerRef = useRef(null);
  
  // Track scroll positions per tab
  const { addManualItem } = useBilling();
  // Track if we're in the middle of a tab change
  const isTabChangeRef = useRef(false);
  
  const pendingDirtyStateRef = useRef(null);

  const sRef = useRef(null);
  const oRef = useRef(null);
  const aRef = useRef(null);
  const pRef = useRef(null);
  const billingRef = useRef(null);
  
  const isBillingActive = activeTab === "B";

  const [sectionDirtyStates, setSectionDirtyStates] = useState({
    S: false,
    O: false,
    A: false,
    P: false,
    B: false,
  });

  // ─── Forward live billing summary up to the drawer/header ─────────────
  useEffect(() => {
    if (billingSummary) {
      onBillingSummaryChange?.(billingSummary);
    }
  }, [billingSummary, onBillingSummaryChange]);

  // ─── Session hydration: ignore spurious dirty signals while tabs load ───
  const beginSessionHydration = useCallback(() => {
    isSessionHydratingRef.current = true;
    isHydratingRef.current = true;
    isInitialMountRef.current = true;
    if (hydrationTimerRef.current) {
      clearTimeout(hydrationTimerRef.current);
    }
    hydrationTimerRef.current = setTimeout(() => {
      isSessionHydratingRef.current = false;
      isHydratingRef.current = false;
      isInitialMountRef.current = false;
      hydrationTimerRef.current = null;
    }, 1500);
  }, []);

  useEffect(() => {
    beginSessionHydration();
    setSectionDirtyStates({
      S: false,
      O: false,
      A: false,
      P: false,
      B: false,
    });
    setHasChanges(false);
    setSaveStatus("saved");
    pendingDirtyStateRef.current = false;
    onDirtyChange?.(false);

    return () => {
      if (hydrationTimerRef.current) {
        clearTimeout(hydrationTimerRef.current);
        hydrationTimerRef.current = null;
      }
    };
  }, [refId, beginSessionHydration, onDirtyChange]);

  // ─── Reset dirty state when initial data loads ──────────────────────────
  useEffect(() => {
    if (initialData) {
      setCurrentInitialData(initialData);
      if (!lastSavedDataRef.current) {
        lastSavedDataRef.current = JSON.stringify({
          subjective: initialData.subjective,
          objective: initialData.objective,
          assessment: initialData.assessment,
          plan: initialData.plan,
        });
      }
    }

    setSectionDirtyStates({
      S: false,
      O: false,
      A: false,
      P: false,
      B: false,
    });
    setHasChanges(false);
    setSaveStatus("saved");
    pendingDirtyStateRef.current = false;
    onDirtyChange?.(false);
    beginSessionHydration();
  }, [initialData, onDirtyChange, beginSessionHydration]);

  const handleBillingChange = useCallback(() => {
    // Do not bump billingRefreshKey here — that force-refetches billing and
    // wipes in-flight manual adds before autosave completes. Header totals
    // already update via onSummaryChange; SOAP save bumps refresh explicitly.
    if (onBillingChange) {
      onBillingChange();
    }
  }, [onBillingChange]);

  const getSavedPlanSnapshot = useCallback(() => {
    if (lastSavedDataRef.current) {
      try {
        const lastSaved = JSON.parse(lastSavedDataRef.current);
        if (lastSaved?.plan) return lastSaved.plan;
      } catch (e) {
        // Ignore parse errors
      }
    }
    return currentInitialData?.plan || initialData?.plan || null;
  }, [currentInitialData, initialData]);

  // Preserve plan/prescription when UI reads empty (avoid wiping saved procedures/products).
  const mergePlanFromLastSavedIfNeeded = useCallback((planData = {}) => {
    const savedPlan = getSavedPlanSnapshot();
    if (!savedPlan) return planData;

    const livePrescription = planData.prescription || { medications: [], notes: "" };
    const savedPrescription = savedPlan.prescription || { medications: [], notes: "" };

    // If Plan wasn't edited, prefer saved text fields; always protect non-empty lists
    // from empty live reads (common when Mark Complete runs from another tab).
    const preferLiveText = sectionDirtyStates.P;

    return {
      ...planData,
      diagnosticsOrdered: preferLiveText
        ? (planData.diagnosticsOrdered || savedPlan.diagnosticsOrdered || "")
        : (planData.diagnosticsOrdered || savedPlan.diagnosticsOrdered || ""),
      plan: preferLiveText
        ? (planData.plan || savedPlan.plan || "")
        : (planData.plan || savedPlan.plan || ""),
      proceduresList:
        planData.proceduresList?.length > 0
          ? planData.proceduresList
          : (savedPlan.proceduresList || []),
      planProducts:
        planData.planProducts?.length > 0
          ? planData.planProducts
          : (savedPlan.planProducts || []),
      appliedTreatmentPlan:
        planData.appliedTreatmentPlan ?? savedPlan.appliedTreatmentPlan ?? null,
      recommendations:
        planData.recommendations?.quickRecommendations?.length > 0 ||
        planData.recommendations?.notes
          ? planData.recommendations
          : (savedPlan.recommendations || { quickRecommendations: [], notes: "" }),
      followUpData:
        planData.followUpData?.length > 0
          ? planData.followUpData
          : (savedPlan.followUpData || []),
      prescription: {
        medications:
          livePrescription.medications?.length > 0
            ? livePrescription.medications
            : (savedPrescription.medications || []),
        notes: livePrescription.notes ?? savedPrescription.notes ?? "",
      },
      prescriptionRef: planData.prescriptionRef || savedPlan.prescriptionRef || null,
    };
  }, [sectionDirtyStates.P, getSavedPlanSnapshot]);

  // ─── Auto-save function with data validation ─────────────────────────────
  const performAutoSave = useCallback(async () => {
    if (readOnly) {
      setSaveStatus("saved");
      return;
    }
    if (isAutoSavingRef.current) {
      return;
    }

    const hasPendingChanges = pendingDirtyStateRef.current === true;
    
    if (!hasPendingChanges) {
      setSaveStatus("saved");
      return;
    }

    isAutoSavingRef.current = true;
    setSaveStatus("saving");
    setIsSaving(true);

    try {
      const soapPayload = forwardedRef.current?.getSoapPayload?.();
      
      if (!soapPayload) {
        isAutoSavingRef.current = false;
        setIsSaving(false);
        setSaveStatus("dirty");
        return;
      }

      const s = soapPayload?.S || {};
      const o = soapPayload?.O || {};
      const a = soapPayload?.A || {};
      const p = mergePlanFromLastSavedIfNeeded(soapPayload?.P || {});

      const labTests = o.labTests || p.labTests || [];

      // ─── DATA VALIDATION: Prevent empty data from overwriting ────────────
      const hasAnyData = 
        s.problemDuration || s.diet || s.activityLevel || 
        s.structuredEntries?.length > 0 ||
        o.vitals?.length > 0 || o.examinationNotes ||
        o.labTests?.length > 0 ||
        a.diagnosis ||
        p.proceduresList?.length > 0 || p.planProducts?.length > 0 ||
        p.prescription?.medications?.length > 0 ||
        p.followUpData?.length > 0 ||
        p.recommendations?.quickRecommendations?.length > 0 ||
        p.recommendations?.notes ||
        p.plan ||
        p.diagnosticsOrdered;

      if (!hasAnyData && lastSavedDataRef.current) {
        try {
          const lastSaved = JSON.parse(lastSavedDataRef.current);
          const hadData = 
            lastSaved.plan?.proceduresList?.length > 0 || 
            lastSaved.plan?.planProducts?.length > 0 ||
            lastSaved.subjective?.structuredEntries?.length > 0 ||
            lastSaved.objective?.vitals?.length > 0;
          
          if (hadData) {
            console.warn('⚠️ [AUTO-SAVE] Payload appears empty but previously had data. Skipping to prevent data loss.');
            setSaveStatus("dirty");
            isAutoSavingRef.current = false;
            setIsSaving(false);
            return;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }

      const payload = {
        booking_ref_id: refId,
        vendor_pet_id: vendorPetId,
        order_id: orderId,
        
        subjective: {
          problemDuration: s.problemDuration || "",
          diet: s.diet || "",
          activityLevel: s.activityLevel || "",
          waterIntake: s.waterIntake || "",
          defecation: s.defecation || "",
          vaccinationStatus: s.vaccinationStatus || "",
          vomiting: s.vomiting || {},
          medicalConditions: s.medicalConditions || [],
          structuredEntries: s.structuredEntries || [],
        },
        objective: {
          vitals: o.vitals || [],
          examinationNotes: o.examinationNotes || "",
          examFindings: o.examFindings || {},
          attachments: (o.attachments || []).map((att) => ({
            attachmentId: att.id || att.attachmentId,
            name: att.name || "",
            label: att.label || "",
            fileType: att.type || "other",
            storageUrl: att.url || "",
            extractedText: att.extractedText || "",
          })),
          labTests: labTests,
        },
        assessment: {
          diagnosis: a.diagnosis || "",
          severity: a.severity || "",
          etiology: a.etiology || "",
          differentialsList: a.differentialDiagnosis ? [a.differentialDiagnosis] : [],
          problemList: a.problemList || [],
          prognosis: a.prognosis || "",
          clinicalNotes: a.clinicalNotes || "",
        },
        plan: {
          diagnosticsOrdered: p.diagnosticsOrdered || "",
          plan: p.plan || "",
          proceduresList: p.proceduresList || [],
          planProducts: p.planProducts || [],
          appliedTreatmentPlan: p.appliedTreatmentPlan || null,
          labTests: labTests,
          recommendations: p.recommendations || { quickRecommendations: [], notes: "" },
          followUpData: p.followUpData || [],
          prescription: p.prescription || { medications: [], notes: "" },
          prescriptionRef: p.prescriptionRef || null,
        },
        status: "in_progress",
      };

      const hasAnyDataInPayload = 
        payload.subjective.problemDuration || 
        payload.subjective.diet || 
        payload.subjective.activityLevel ||
        payload.subjective.structuredEntries?.length > 0 ||
        payload.objective.vitals?.length > 0 ||
        payload.objective.examinationNotes ||
        payload.objective.labTests?.length > 0 ||
        payload.assessment.diagnosis ||
        payload.plan.proceduresList?.length > 0 ||
        payload.plan.planProducts?.length > 0 ||
        payload.plan.prescription?.medications?.length > 0 ||
        payload.plan.followUpData?.length > 0 ||
        payload.plan.recommendations?.quickRecommendations?.length > 0 ||
        payload.plan.recommendations?.notes ||
        payload.plan.plan ||
        payload.plan.diagnosticsOrdered;

      if (!hasAnyDataInPayload && lastSavedDataRef.current) {
        try {
          const lastSaved = JSON.parse(lastSavedDataRef.current);
          const hadData = 
            lastSaved.plan?.proceduresList?.length > 0 || 
            lastSaved.plan?.planProducts?.length > 0 ||
            lastSaved.subjective?.structuredEntries?.length > 0 ||
            lastSaved.objective?.vitals?.length > 0;
          
          if (hadData) {
            console.warn('⚠️ [AUTO-SAVE] Final payload appears empty. Skipping to prevent data loss.');
            setSaveStatus("dirty");
            isAutoSavingRef.current = false;
            setIsSaving(false);
            return;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }

      const response = await httpService.post("/vendorpet/addvendorsoap", payload);

      if (response.code === 200) {
        forwardedRef.current?.resetDirty?.();
        pendingDirtyStateRef.current = false;
        setHasChanges(false);
        setSaveStatus("saved");
        onDirtyChange?.(false);
        
        lastSavedDataRef.current = JSON.stringify(payload);

        setBillingRefreshKey(prev => prev + 1);
        forwardedRef.current?.refreshBilling?.();
        
      } else {
        throw new Error(response.message || "Auto-save failed");
      }
    } catch (err) {
      console.error("Auto-save failed:", err);
      setSaveStatus("dirty");
    } finally {
      isAutoSavingRef.current = false;
      setIsSaving(false);
    }
  }, [refId, vendorPetId, orderId, forwardedRef, onDirtyChange, mergePlanFromLastSavedIfNeeded]);

  // ─── Debounced auto-save trigger ────────────────────────────────────────
  const triggerAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    pendingDirtyStateRef.current = true;
    setSaveStatus("dirty");

    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSave();
      autoSaveTimerRef.current = null;
    }, 2000);
  }, [performAutoSave]);

  // ─── Handle section dirty changes ──────────────────────────────────────
  const handleSectionDirtyChange = useCallback((section, isDirty) => {
    if (readOnly) return;
    if (isSessionHydratingRef.current) return;

    setSectionDirtyStates(prev => ({ ...prev, [section]: isDirty }));

    if (isDirty) {
      if (section !== "B") {
        triggerAutoSave();
      }
    } else {
      setSectionDirtyStates(prev => {
        const stillDirty = Object.values(prev).some(v => v === true);
        if (!stillDirty) pendingDirtyStateRef.current = false;
        return prev;
      });
    }
  }, [triggerAutoSave]);

  // ─── Update parent about dirty state ──────────────────────────────────
  useEffect(() => {
    if (isSessionHydratingRef.current) return;

    const anyDirty = Object.values(sectionDirtyStates).some(v => v === true);
    setHasChanges(anyDirty);
    onDirtyChange?.(anyDirty);

    if (!anyDirty && saveStatus !== "saving") {
      setSaveStatus("saved");
      pendingDirtyStateRef.current = false;
    }
  }, [sectionDirtyStates, onDirtyChange, saveStatus]);

  // ─── Clean up timer on unmount ─────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, []);

  // ─── Handle external refresh trigger ──────────────────────────────────
  useEffect(() => {
    if (externalRefreshBilling > 0) {
      setBillingRefreshKey(prev => prev + 1);
      if (!sectionDirtyStates.P) {
        refreshPlanData();
      }
    }
  }, [externalRefreshBilling]);

  // ─── Refresh plan data function ────────────────────────────────────────
  const refreshPlanData = useCallback(async () => {
    const now = Date.now();
    if (now - lastRefreshTime.current < 1000) return;
    lastRefreshTime.current = now;

    if (isRefreshingPlan) return;

    if (sectionDirtyStates.P) {
      return;
    }

    setIsRefreshingPlan(true);
    isHydratingRef.current = true;
    
    try {
      const response = await httpService.get(`/vendorpet/getvendorsoap/${refId}`);
      
      if (response?.data) {
        setCurrentInitialData(response.data);
        if (!sectionDirtyStates.P) {
          lastSavedDataRef.current = JSON.stringify({
            subjective: response.data.subjective,
            objective: response.data.objective,
            assessment: response.data.assessment,
            plan: response.data.plan,
          });
        }
        setPlanRefreshKey(prev => prev + 1);
        forwardedRef.current?.resetDirty?.();
        setHasChanges(false);
        setSaveStatus("saved");
        pendingDirtyStateRef.current = false;
        onDirtyChange?.(false);
      }
    } catch (err) {
      console.error("Failed to refresh plan data:", err);
    } finally {
      setIsRefreshingPlan(false);
      setTimeout(() => { 
        isHydratingRef.current = false;
      }, 300);
    }
  }, [refId, isRefreshingPlan, forwardedRef, onDirtyChange, sectionDirtyStates.P]);

  // ─── Refresh plan data when switching to Plan tab ─────────────────────
  useEffect(() => {
    if (activeTab === "P" && planRefreshKey > 0) {
      const hasUnsavedChanges = Object.values(sectionDirtyStates).some(v => v === true);
      if (!hasUnsavedChanges) {
        refreshPlanData();
      }
    }
  }, [activeTab, planRefreshKey, sectionDirtyStates, refreshPlanData]);

  // ─── Manual SAVE HANDLER ──────────────────────────────────────────────
  const handleSave = useCallback(async (shouldComplete = false) => {
    if (readOnly && !shouldComplete) {
      return true;
    }
    if (readOnly && shouldComplete) {
      toast.error("This consultation is closed and cannot be edited");
      return false;
    }
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    setIsSaving(true);
    setSaveStatus("saving");
    
    try {
      // Flush billing first so procedures/meds/labs are on the order before complete
      await billingRef.current?.flushSave?.();

      const soapPayload = forwardedRef.current?.getSoapPayload?.();

      if (!soapPayload) {
        toast.error("Could not read SOAP data — please try again");
        setSaveStatus("dirty");
        return false;
      }

      const s = soapPayload?.S || {};
      const o = soapPayload?.O || {};
      const a = soapPayload?.A || {};
      const p = mergePlanFromLastSavedIfNeeded(soapPayload?.P || {});

      const labTests = o.labTests || p.labTests || [];

      const payload = {
        booking_ref_id: refId,
        vendor_pet_id: vendorPetId,
        order_id: orderId,
        
        subjective: {
          problemDuration: s.problemDuration || "",
          diet: s.diet || "",
          activityLevel: s.activityLevel || "",
          waterIntake: s.waterIntake || "",
          defecation: s.defecation || "",
          vaccinationStatus: s.vaccinationStatus || "",
          vomiting: s.vomiting || {},
          medicalConditions: s.medicalConditions || [],
          structuredEntries: s.structuredEntries || [],
        },
        objective: {
          vitals: o.vitals || [],
          examinationNotes: o.examinationNotes || "",
          examFindings: o.examFindings || {},
          attachments: (o.attachments || []).map((att) => ({
            attachmentId: att.id || att.attachmentId,
            name: att.name || "",
            label: att.label || "",
            fileType: att.type || "other",
            storageUrl: att.url || "",
            extractedText: att.extractedText || "",
          })),
          labTests: labTests,
        },
        assessment: {
          diagnosis: a.diagnosis || "",
          severity: a.severity || "",
          etiology: a.etiology || "",
          differentialsList: a.differentialDiagnosis ? [a.differentialDiagnosis] : [],
          problemList: a.problemList || [],
          prognosis: a.prognosis || "",
          clinicalNotes: a.clinicalNotes || "",
        },
        plan: {
          diagnosticsOrdered: p.diagnosticsOrdered || "",
          plan: p.plan || "",
          proceduresList: p.proceduresList || [],
          planProducts: p.planProducts || [],
          appliedTreatmentPlan: p.appliedTreatmentPlan || null,
          labTests: labTests,
          recommendations: p.recommendations || { quickRecommendations: [], notes: "" },
          followUpData: p.followUpData || [],
          prescription: p.prescription || { medications: [], notes: "" },
          prescriptionRef: p.prescriptionRef || null,
        },
        status: shouldComplete ? "completed" : "in_progress",
      };

      const response = await httpService.post("/vendorpet/addvendorsoap", payload);

      if (response.code === 200) {
        forwardedRef.current?.resetDirty?.();
        setHasChanges(false);
        setSaveStatus("saved");
        pendingDirtyStateRef.current = false;
        onDirtyChange?.(false);
        lastSavedDataRef.current = JSON.stringify(payload);
        
        setBillingRefreshKey(prev => prev + 1);
        forwardedRef.current?.refreshBilling?.();

        toast.success(shouldComplete ? "Consultation completed" : "Progress saved");
        return true;
      }
    } catch (err) {
      console.error("Failed to save SOAP note:", err);
      toast.error(err.response?.data?.message || "Failed to save progress");
      setSaveStatus("dirty");
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [refId, vendorPetId, orderId, forwardedRef, onDirtyChange, mergePlanFromLastSavedIfNeeded]);

  // ─── Expose methods to parent ──────────────────────────────────────────
  useImperativeHandle(forwardedRef, () => ({
    getSoapPayload: () => {
      const sData = sRef.current?.getData?.() ?? {};
      const oData = oRef.current?.getData?.() ?? {};
      const aData = aRef.current?.getData?.() ?? {};
      const pData = mergePlanFromLastSavedIfNeeded(pRef.current?.getData?.() ?? {});

      return {
        S: sData,
        O: { ...oData },
        A: aData,
        P: pData,
      };
    },
    getBillingData: () => billingRef.current?.getData?.() ?? null,
    getBillingRef: () => billingRef,
    resetDirty: () => {
      sRef.current?.resetDirty?.();
      oRef.current?.resetDirty?.();
      aRef.current?.resetDirty?.();
      pRef.current?.resetDirty?.();
      billingRef.current?.resetDirty?.();
      setSectionDirtyStates({
        S: false,
        O: false,
        A: false,
        P: false,
        B: false,
      });
      setHasChanges(false);
      setSaveStatus("saved");
      pendingDirtyStateRef.current = false;
      onDirtyChange?.(false);
    },
    getSectionDirtyStates: () => sectionDirtyStates,
    refreshBilling: () => {
      setBillingRefreshKey(prev => prev + 1);
    },
    refreshPlan: () => {
      if (!sectionDirtyStates.P) {
        refreshPlanData();
      }
    },
    save: handleSave,
    isSaving: () => isSaving,
    hasChanges: () => hasChanges,
    getSaveStatus: () => saveStatus,
  }));

  const tabs = [
    { id: "S", label: "Subjective", color: "#6d28d9", icon: "S" },
    { id: "O", label: "Objective", color: "#0f766e", icon: "O" },
    { id: "A", label: "Assessment", color: "#b45309", icon: "A" },
    { id: "P", label: "Plan", color: "#15803d", icon: "P" },
    { id: "B", label: "Billing", color: "#0d9488", icon: "₹" },
  ];

  // const scrollToSection = useCallback((tabId) => {
  //   const container = scrollContainerRef.current;
  //   const element = document.getElementById(`soap-section-${tabId}`);
  //   if (!container || !element) return;

  //   if (activeTab && activeTab !== tabId) {
  //     scrollPositionsRef.current[activeTab] = container.scrollTop;
  //   }

  //   const containerRect = container.getBoundingClientRect();
  //   const elementRect = element.getBoundingClientRect();
  //   const scrollTop = container.scrollTop + (elementRect.top - containerRect.top) - 8;

  //   container.scrollTo({ top: Math.max(0, scrollTop), behavior: "smooth" });
  // }, [activeTab]);

  // const restoreScrollPosition = useCallback((tabId) => {
  //   const savedPosition = scrollPositionsRef.current[tabId];
  //   if (savedPosition !== undefined && scrollContainerRef.current) {
  //     setTimeout(() => {
  //       scrollContainerRef.current.scrollTop = savedPosition;
  //     }, 100);
  //   }
  // }, []);

  const handleTabClick = useCallback((tabId) => {
    if (tabId === activeTab) return;

    if (tabId === "B") {
      if (pendingDirtyStateRef.current && autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
        performAutoSave();
      }
      setBillingRefreshKey((prev) => prev + 1);
      setActiveTab(tabId);
      setExpandedSections(["B"]);
      return;
    }

    if (tabId === "P" && activeTab !== "P") {
      const hasUnsavedChanges = Object.values(sectionDirtyStates).some(v => v === true);
      if (!hasUnsavedChanges) {
        refreshPlanData();
      }
    }

    setActiveTab(tabId);
    setExpandedSections([tabId]);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0; // always land at the top of the new panel
    }
  }, [activeTab, refreshPlanData, sectionDirtyStates, performAutoSave]);

  // ⭐ FIX: Toggle section - accordion behavior
  // const toggleSection = useCallback((sectionId) => {
  //   const isOpen = expandedSections.includes(sectionId);
    
  //   if (isOpen) {
  //     setExpandedSections(prev => prev.filter(id => id !== sectionId));
  //   } else {
  //     setExpandedSections([sectionId]);
  //     setActiveTab(sectionId);
  //   }
  // }, [expandedSections]);

  // ⭐ FIX: Scroll-spy that respects tab changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || isBillingActive) return;

    const sectionIds = ["S", "O", "A", "P"];

    const handleScroll = () => {
      if (programmaticScrollRef.current || isTabChangeRef.current) return;

      const scrollPos = container.scrollTop + 20;
      let currentId = null;

      for (const id of sectionIds) {
        if (!expandedSections.includes(id)) continue;
        const el = document.getElementById(`soap-section-${id}`);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const elementTop = rect.top - containerRect.top + container.scrollTop;
        
        if (elementTop <= scrollPos) {
          currentId = id;
        }
      }

      if (currentId && currentId !== activeTab && !isTabChangeRef.current) {
        setActiveTab(currentId);
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [expandedSections, activeTab, isBillingActive]);

  const isExpanded = useCallback((sectionId) => expandedSections.includes(sectionId), [expandedSections]);

  return (
    <div className="flex flex-col h-full pl-2">
      {readOnly && (
        <style>{`
          .soap-view-only input,
          .soap-view-only textarea,
          .soap-view-only select {
            pointer-events: none !important;
            background-color: #f8fafc !important;
            cursor: default !important;
          }
          .soap-view-only button:not([data-nav-tab]),
          .soap-view-only [role="button"]:not([data-nav-tab]) {
            pointer-events: none !important;
            opacity: 0.65;
          }
          .soap-view-only button[data-nav-tab],
          .soap-view-only [data-nav-tab] {
            pointer-events: auto !important;
            opacity: 1 !important;
            cursor: pointer !important;
          }
        `}</style>
      )}
      {/* ─── Tab Bar with Auto-Save Status ────────────────────────────── */}
      <div className="shrink-0 bg-slate-100/70 border-b rounded-lg px-2 sm:px-4 mt-2">
        <div className="flex items-center justify-between gap-2 py-2">
          <div className="flex items-center gap-0.5 overflow-x-auto scrollbar-none">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              const isExpanded_ = isExpanded(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`shrink-0 flex items-center gap-1.5 px-2 sm:px-3.5 py-1.5 rounded-full text-[11px] sm:text-[12px] font-bold transition-all duration-150 whitespace-nowrap ${
                    isActive ? "text-white shadow-sm" : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                  }`}
                  style={isActive ? { backgroundColor: tab.color } : {}}
                >
                  <span
                    className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] sm:text-[11px] font-black transition-all shrink-0 ${
                      isActive ? "bg-white/20" : "bg-slate-100"
                    }`}
                    style={!isActive ? { color: tab.color } : {}}
                  >
                    {tab.icon || tab.id}
                  </span>
                  <span className="hidden sm:inline">{tab.label}</span>
                  {tab.id === "B" && billingSummary?.totalCharges > 0 && (
                    <span
                      className={`text-[12px] font-bold px-1.5 py-1 rounded-full ${
                        isActive ? "bg-white/20 text-white" : "bg-slate-100"
                      }`}
                      style={!isActive ? { color: tab.color } : {}}
                    >
                      ₹{billingSummary.totalCharges.toFixed(0)}
                    </span>
                  )}
                  {isExpanded_ && !isActive && tab.id !== "B" && (
                    <span className="w-1.5 h-1.5 rounded-full bg-white/50 inline-block ml-1" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="shrink-0">
            {readOnly ? (
              <span className="text-[11px] font-semibold text-slate-500 px-2">
                View only
              </span>
            ) : (
              <AutoSaveStatus status={saveStatus} isSaving={isSaving} />
            )}
          </div>
        </div>
        <style>{`
          .scrollbar-none { -ms-overflow-style: none; scrollbar-width: none; }
          .scrollbar-none::-webkit-scrollbar { display: none; }
        `}</style>
      </div>

      {/* ─── SOAP Content — only the active section is ever rendered ────────── */}
      <div
        ref={scrollContainerRef}
        className={`flex-1 overflow-y-auto bg-white py-2 px-2 sm:px-2 ${isBillingActive ? "hidden" : ""} ${readOnly ? "soap-view-only" : ""}`}
      >

        <div className={activeTab === "S" ? "" : "hidden"}>
          <Subjective
            ref={sRef}
            refId={refId}
            vendorPetId={vendorPetId}
            orderId={orderId}
            isExpanded={true}
            onToggle={() => {}}
            onDirtyChange={(isDirty) => handleSectionDirtyChange("S", isDirty)}
            initialData={currentInitialData?.subjective || initialData?.subjective}
            readOnly={readOnly}
          />
        </div>

        <div className={activeTab === "O" ? "" : "hidden"}>
          <Objective
            ref={oRef}
            refId={refId}
            vendorPetId={vendorPetId}
            orderId={orderId}
            isExpanded={true}
            onToggle={() => {}}
            onDirtyChange={(isDirty) => handleSectionDirtyChange("O", isDirty)}
            initialData={currentInitialData?.objective || initialData?.objective}
            isHydratingRef={isHydratingRef}
            addManualItem={addManualItem}
            readOnly={readOnly}
          />
        </div>

        <div className={activeTab === "A" ? "" : "hidden"}>
          <Assessment
            ref={aRef}
            refId={refId}
            vendorPetId={vendorPetId}
            orderId={orderId}
            isExpanded={true}
            onToggle={() => {}}
            onDirtyChange={(isDirty) => handleSectionDirtyChange("A", isDirty)}
            initialData={currentInitialData?.assessment || initialData?.assessment}
            readOnly={readOnly}
          />
        </div>

        <div className={activeTab === "P" ? "" : "hidden"}>
          <Plan
            ref={pRef}
            refId={refId}
            vendorPetId={vendorPetId}
            orderId={orderId}
            isExpanded={true}
            onToggle={() => {}}
            onDirtyChange={(isDirty) => handleSectionDirtyChange("P", isDirty)}
            initialData={currentInitialData?.plan || initialData?.plan}
            refreshKey={planRefreshKey}
            onPlanChange={() => handleSectionDirtyChange("B", true)}
            isHydratingRef={isHydratingRef}
            readOnly={readOnly}
          />
        </div>
      </div>

      <div className={`flex-1 overflow-y-auto py-2 pr-5 bg-white ${isBillingActive ? "" : "hidden"} ${readOnly ? "soap-view-only" : ""}`}>
        <BillingTab
          ref={billingRef}
          refId={refId}
          vendorPetId={vendorPetId}
          orderId={orderId}
          consultationFee={consultationFee}
          onDirtyChange={(isDirty) => handleSectionDirtyChange("B", isDirty)}
          onSummaryChange={setBillingSummary}
          refreshTrigger={billingRefreshKey}
          onBillingChange={handleBillingChange}
          readOnly={readOnly}
          isActive={isBillingActive}
        />
      </div>
    </div>
  );
};

export default VetStartedSOAPTabs;